import { Box, Divider } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useToastProvider } from '../../shared/ToastProvider';
import { useEffect, useReducer, useState } from 'react';
import { fetchJsonWithAuth } from '../../shared/fetch-json.util';
import MessageBanner from '../../shared/MessageBanner';
import { useStatusMessages } from '../../shared/StatusMessages';
import { useAPIProgress } from '../../shared/APIProgress';
import { FileUploadCard } from '../../shared/FileUploadCard';
import { useUserInfo } from '../../shared/UserInfo';
import { FileUpload } from '../../shared/FileUpload';
import { EntityLinksList } from '../../shared/EntityLinksList';
import { ATTACHMENT, ATTACHMENTS_TITLE } from '../../shared/AppConstants';
import { saveFileUpload } from '../../shared/UtilFunctions';
import { getEnvVar } from '../../utils/getEnvVar';

const initialState = {
  loading: true,
  error: '',
  post: []
};

export function PhasesAttachments({ userInfoMap, strategyId }) {
  const info = useUserInfo();
  const [attachmentToDelete, setAttachmentToDelete] = useState(null);
  const tokenAvailable = info?.token;
  const [attachmentData, setAttachmentData] = useState([]);
  const { apiInProgressCount, incrementAPIInProgress, decrementAPIInProgress } =
    useAPIProgress();
  const {
    statusMessages,
    setStatusErrorMsg,
    setStatusInfoMsg,
    clearAllMessages,
    setStatusSuccessMsg
  } = useStatusMessages();
  const commonProps = {
    setStatusErrorMsg,
    incrementAPIInProgress,
    decrementAPIInProgress,
    setStatusInfoMsg,
    clearAllMessages,
    setStatusSuccessMsg
  };
  const { enqueueToast } = useToastProvider();

  function populateOtherInfo(current, actionType) {
    if (setAttachmentData && actionType !== 'FETCH_SUCCESS')
      setAttachmentData(current);

    return current;
  }

  const reducer = (state, action) => {
    switch (action.type) {
      case 'FETCH_SUCCESS':
        return {
          loading: false,
          post: populateOtherInfo(action.payload),
          error: ''
        };
      case 'FETCH_FAIL':
        return {
          loading: false,
          post: [],
          error: 'Something went wrong!'
        };
      case 'add': {
        let objArray = [...state.post].concat(action.payload);

        return {
          loading: false,
          post: populateOtherInfo(objArray, action.type),
          error: ''
        };
      }
      case 'edit': {
        let objArray = [...state.post];

        // Find the edited item index
        let index = objArray.findIndex((obj) =>
          obj.number
            ? obj.attachment === action.payload.attachment
            : obj.attachment.name === action.payload.attachment.name
        );

        objArray[index] = action.payload;

        return {
          loading: false,
          post: populateOtherInfo(objArray, action.type),
          error: ''
        };
      }
      case 'del': {
        let objArray = [...state.post];

        let indexToDelete = objArray.findIndex(
          (obj) => obj?.number === attachmentToDelete.number
        );
        objArray.splice(indexToDelete, 1);

        return {
          loading: false,
          post: populateOtherInfo(objArray),
          error: ''
        };
      }

      default:
        return state;
    }
  };

  const [, dispatch] = useReducer(reducer, initialState);
  const attachmentApiUrl = `${getEnvVar('VITE_GSS_API_URL')}/${strategyId}/attachments`;

  function fetchAttachments() {
    incrementAPIInProgress();
    fetchJsonWithAuth(attachmentApiUrl)
      .then((response) =>
        dispatch({ type: 'FETCH_SUCCESS', payload: response.json })
      )
      .catch((error) =>
        setStatusErrorMsg(
          "Couldn't load the Attachments. Please try again.",
          error
        )
      )
      .finally(() => decrementAPIInProgress());
  }

  useEffect(() => {
    if (tokenAvailable && strategyId) fetchAttachments();
  }, [tokenAvailable, strategyId]);

  const onSave = (data, event, clearComponent) => {
    const { description, selectedPhases, variableFormContent } = data;

    saveFileUpload(
      event,
      'attachment',
      variableFormContent,
      description,
      attachmentApiUrl,
      dispatch,
      clearComponent,
      selectedPhases,
      commonProps,
      enqueueToast
    );
  };

  return (
    <>
      <MessageBanner
        statusMessages={statusMessages}
        apiInProgressCount={apiInProgressCount}
      />
      <Grid mt={4}>
        <FileUploadCard
          title={ATTACHMENTS_TITLE}
          entity={ATTACHMENT}
          onSave={onSave}
          commonProps={commonProps}
          VariableForm={FileUpload}
          count={attachmentData?.length ?? 0}
          additionalContent={
            <Box mt={3}>
              {attachmentData?.length > 0 && (
                <Divider style={{ marginTop: '1em' }} />
              )}
              <EntityLinksList
                key={attachmentData}
                entityName={ATTACHMENT}
                apiUrl={`${getEnvVar('VITE_GSS_API_URL')}/${strategyId}/attachments`}
                setEntityData={setAttachmentData}
                entityData={attachmentData}
                userInfoMap={userInfoMap}
                isEdit={true}
                commonProps={commonProps}
                dispatch={dispatch}
                setEntityToDelete={setAttachmentToDelete}
                entityToDelete={attachmentToDelete}
                showHideEnabled={true}
                enqueueToast={enqueueToast}
              />
            </Box>
          }
        />
      </Grid>
    </>
  );
}
